/**
 * TaxExpensePage
 * 매입/경비 관리 페이지
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTaxExpenses } from '../../use-cases/useTaxExpenses';
import { TaxExpense, formatKRW, getSourceLabel } from '../../models/Tax';
import { ExpenseFormModal } from './ExpenseFormModal';
import { CsvUploadModal } from './CsvUploadModal';

export const TaxExpensePage: React.FC = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showForm, setShowForm] = useState(false);
  const [showCsv, setShowCsv] = useState(false);
  const [editItem, setEditItem] = useState<TaxExpense | null>(null);
  const [bulkCategoryId, setBulkCategoryId] = useState('');

  const {
    expenses,
    categories,
    loading,
    error,
    summary,
    fetchExpenses,
    fetchCategories,
    createExpense,
    updateExpense,
    deleteExpense,
    bulkDelete,
    bulkUpdateCategory,
    uploadCsv,
    exportCsv,
  } = useTaxExpenses();

  const loadData = useCallback(() => {
    fetchExpenses({ year, month, category: categoryFilter || undefined, search: search || undefined });
  }, [fetchExpenses, year, month, categoryFilter, search]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // 전체 선택
  const allSelected = expenses.length > 0 && selectedIds.size === expenses.length;
  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(expenses.map((e) => e.id)));
    }
  };
  const toggleOne = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  // 삭제
  const handleDelete = async (id: string) => {
    if (!window.confirm('이 경비를 삭제하시겠습니까?')) return;
    const ok = await deleteExpense(id);
    if (ok) loadData();
  };

  // 일괄 삭제
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`선택한 ${selectedIds.size}건을 삭제하시겠습니까?`)) return;
    const ok = await bulkDelete(Array.from(selectedIds));
    if (ok) {
      setSelectedIds(new Set());
      loadData();
    }
  };

  // 일괄 카테고리 변경
  const handleBulkCategory = async () => {
    if (selectedIds.size === 0 || !bulkCategoryId) return;
    const ok = await bulkUpdateCategory(Array.from(selectedIds), bulkCategoryId);
    if (ok) {
      setSelectedIds(new Set());
      setBulkCategoryId('');
      loadData();
    }
  };

  // 수정 모달
  const handleEdit = (item: TaxExpense) => {
    setEditItem(item);
    setShowForm(true);
  };

  // 추가/수정 제출
  const handleSubmit = async (data: any) => {
    if (editItem) {
      const ok = await updateExpense(editItem.id, data);
      if (ok) loadData();
      return ok;
    } else {
      const ok = await createExpense(data);
      if (ok) loadData();
      return ok;
    }
  };

  // 연도 옵션 (현재년도 ~ 3년 전)
  const yearOptions = Array.from({ length: 4 }, (_, i) => now.getFullYear() - i);

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">매입/경비 관리</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setShowCsv(true)}
            className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100"
          >
            CSV 업로드
          </button>
          <button
            onClick={() => exportCsv(year, month)}
            className="px-4 py-2 text-sm font-medium text-green-700 bg-green-50 rounded-md hover:bg-green-100"
          >
            CSV 내보내기
          </button>
          <button
            onClick={() => {
              setEditItem(null);
              setShowForm(true);
            }}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            + 수동 추가
          </button>
        </div>
      </div>

      {/* 요약 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">건수</p>
          <p className="text-2xl font-bold text-gray-900">{summary.count}건</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">공급가액 합계</p>
          <p className="text-2xl font-bold text-gray-900">{formatKRW(summary.totalAmount)}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">부가세 합계</p>
          <p className="text-2xl font-bold text-blue-600">{formatKRW(summary.totalVat)}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">총 합계</p>
          <p className="text-2xl font-bold text-red-600">{formatKRW(summary.totalSum)}</p>
        </div>
      </div>

      {/* 필터 */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-3 items-center">
          {/* 연/월 선택 */}
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}년</option>
            ))}
          </select>
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>{m}월</option>
            ))}
          </select>

          {/* 카테고리 필터 */}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="">전체 분류</option>
            {categories.filter(c => c.is_active).map((cat) => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>

          {/* 검색 */}
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="사용처/메모 검색..."
            className="px-3 py-2 border border-gray-300 rounded-md text-sm flex-1 min-w-[200px]"
          />
        </div>

        {/* 일괄 작업 바 */}
        {selectedIds.size > 0 && (
          <div className="mt-3 flex items-center gap-3 p-3 bg-blue-50 rounded-md">
            <span className="text-sm font-medium text-blue-700">
              {selectedIds.size}건 선택됨
            </span>
            <button
              onClick={handleBulkDelete}
              className="px-3 py-1 text-xs font-medium text-red-700 bg-red-100 rounded hover:bg-red-200"
            >
              일괄 삭제
            </button>
            <select
              value={bulkCategoryId}
              onChange={(e) => setBulkCategoryId(e.target.value)}
              className="px-2 py-1 border border-gray-300 rounded text-xs"
            >
              <option value="">분류 변경...</option>
              {categories.filter(c => c.is_active).map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            {bulkCategoryId && (
              <button
                onClick={handleBulkCategory}
                className="px-3 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded hover:bg-blue-200"
              >
                적용
              </button>
            )}
          </div>
        )}
      </div>

      {/* 에러 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* 테이블 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">날짜</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">사용처</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">공급가액</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">부가세</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">합계</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">분류</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">출처</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">카드</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">작업</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                    불러오는 중...
                  </td>
                </tr>
              ) : expenses.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                    데이터가 없습니다. CSV를 업로드하거나 수동으로 추가하세요.
                  </td>
                </tr>
              ) : (
                expenses.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(item.id)}
                        onChange={() => toggleOne(item.id)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{item.date}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div>{item.vendor}</div>
                      {item.memo && (
                        <div className="text-xs text-gray-500 truncate max-w-[200px]">{item.memo}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right whitespace-nowrap">
                      {item.amount.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-blue-600 text-right whitespace-nowrap">
                      {item.vat.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right whitespace-nowrap">
                      {item.total.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {item.category_name ? (
                        <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700">
                          {item.category_name}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">미분류</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <span className={`px-2 py-0.5 rounded ${
                        item.source === 'csv'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {getSourceLabel(item.source)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {item.card_last4 ? `*${item.card_last4}` : '-'}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => handleEdit(item)}
                        className="text-xs text-blue-600 hover:text-blue-800 mr-2"
                      >
                        수정
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        삭제
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 모달 */}
      <ExpenseFormModal
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setEditItem(null);
        }}
        onSubmit={handleSubmit}
        categories={categories}
        editItem={editItem}
      />

      <CsvUploadModal
        isOpen={showCsv}
        onClose={() => setShowCsv(false)}
        onUpload={uploadCsv}
        onRefresh={loadData}
      />
    </div>
  );
};
